// ─── Device Performance Detection Utility ───────────────────────────────────
// Multi-layered detection: OS, Hardware APIs, WebGL GPU, Network, Accessibility,
// Screen DPI, and a lightweight micro-benchmark fallback for iOS/Safari.
//
// IMPORTANT DESIGN NOTES:
// • This function is called ONCE per session — results are cached by the hook.
// • deviceMemory is Chromium-only; Safari/Firefox fall back to benchmark scoring.
// • WEBGL_debug_renderer_info may return null in Firefox Private Mode / Safari;
//   GPU detection gracefully degrades to benchmark-only scoring.
// • navigator.platform is deprecated; we prefer User-Agent Client Hints API
//   (navigator.userAgentData) and use platform only as a last-resort fallback.
// ─────────────────────────────────────────────────────────────────────────────

// ── Type declarations for non-standard browser APIs ─────────────────────────

interface NavigatorUAData {
    readonly platform: string;
    readonly mobile: boolean;
    readonly brands: ReadonlyArray<{ brand: string; version: string }>;
}

interface NetworkInformation extends EventTarget {
    readonly effectiveType: string;
    readonly saveData: boolean;
}

interface ExtendedNavigator extends Navigator {
    readonly userAgentData?: NavigatorUAData;
    readonly deviceMemory?: number;
    readonly connection?: NetworkInformation;
}

interface WebGLDebugRendererInfo {
    readonly UNMASKED_VENDOR_WEBGL: number;
    readonly UNMASKED_RENDERER_WEBGL: number;
}

// ── Public types ────────────────────────────────────────────────────────────

export type PerformanceTier = 'low' | 'medium' | 'high';

export interface DeviceSpecs {
    os: 'iOS' | 'Android' | 'Windows' | 'macOS' | 'Linux' | 'Unknown';
    deviceType: 'Mobile' | 'Tablet' | 'Desktop';
    cpuCores: number;
    ramGB: number;
    gpu: string;
    gpuVendor: string;
    isLowSpec: boolean;
    performanceTier: PerformanceTier;
    benchmarkTimeMs: number;
    prefersReducedMotion: boolean;
    networkEffectiveType: string;
    saveData: boolean;
    screenPixelRatio: number;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Resolve OS from User-Agent Client Hints (modern) with UA string fallback.
 * navigator.userAgentData.platform is the modern replacement for the
 * deprecated navigator.platform.
 */
function detectOS(nav: ExtendedNavigator): DeviceSpecs['os'] {
    const ua = nav.userAgent;

    // Modern: User-Agent Client Hints API (Chrome 90+, Edge 90+)
    const uaPlatform = nav.userAgentData?.platform?.toLowerCase() || '';
    if (uaPlatform) {
        if (uaPlatform === 'ios') return 'iOS';
        if (uaPlatform === 'android') return 'Android';
        if (uaPlatform === 'windows') return 'Windows';
        if (uaPlatform === 'macos' || uaPlatform === 'chromeos') return 'macOS';
        if (uaPlatform === 'linux') return 'Linux';
    }

    // Fallback: UA string parsing (works on all browsers including Safari/Firefox)
    if (/iPad|iPhone|iPod/.test(ua) || (nav.maxTouchPoints > 1 && /Mac/.test(ua))) return 'iOS';
    if (/Android/.test(ua)) return 'Android';
    if (/Windows/.test(ua)) return 'Windows';
    if (/Macintosh|Mac OS/.test(ua)) return 'macOS';
    if (/Linux/.test(ua)) return 'Linux';

    return 'Unknown';
}

function detectDeviceType(nav: ExtendedNavigator): DeviceSpecs['deviceType'] {
    const ua = nav.userAgent;
    const isTablet = /Tablet|iPad|PlayBook|Silk/.test(ua)
        || (nav.maxTouchPoints > 1 && /Mac/.test(ua));
    if (isTablet) return 'Tablet';

    // userAgentData.mobile is the modern reliable check
    if (nav.userAgentData?.mobile) return 'Mobile';
    if (/Mobi|Android|iPhone|iPod/.test(ua)) return 'Mobile';

    return 'Desktop';
}

/**
 * Extract GPU renderer string via WebGL.
 * WEBGL_debug_renderer_info can be null in Firefox Private Mode, Safari, or
 * some Android browsers — in that case gpu/gpuVendor stay as 'Unknown'.
 * The WebGL context is explicitly released after use to free GPU memory.
 */
function detectGPU(): { gpu: string; gpuVendor: string; isLowEndGPU: boolean } {
    let gpu = 'Unknown GPU';
    let gpuVendor = 'Unknown Vendor';
    let isLowEndGPU = false;

    try {
        const canvas = document.createElement('canvas');
        const gl = (canvas.getContext('webgl2') || canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;

        if (gl) {
            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info') as WebGLDebugRendererInfo | null;

            if (debugInfo) {
                gpu = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
                gpuVendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '';

                // Match known low-end GPU families.
                // NOTE: GPU string matching is inherently fragile since vendors
                // can change naming at any time. We use broad family patterns
                // and rely on benchmark scoring as the primary fallback.
                const lowEndPatterns = [
                    /swiftshader/i,                     // Chrome software fallback
                    /software|llvmpipe|mesa.*swrast/i,   // OS-level software renderers
                    /microsoft basic/i,                  // Windows without GPU driver
                    /powervr/i,                          // Old PowerVR (budget phones, old iPhones)
                    /mali-t|mali-4|mali-g3[12]|mali-g5[12]/i,  // Budget ARM Mali
                    /adreno\s*[2-4]\d{2}|adreno\s*50[56]|adreno\s*6[12]\d/i, // Budget Qualcomm Adreno
                ];
                isLowEndGPU = lowEndPatterns.some(p => p.test(gpu));
            }

            // Cleanup: release WebGL context to free GPU memory
            const loseCtx = gl.getExtension('WEBGL_lose_context');
            if (loseCtx) loseCtx.loseContext();
        }
    } catch (e) {
        console.warn('WebGL GPU detection failed:', e);
    }

    return { gpu, gpuVendor, isLowEndGPU };
}

/**
 * Lightweight micro-benchmark: measures JS execution speed.
 * 250 K iterations of sin/cos — takes ~3-5 ms on modern hardware,
 * ~10-20 ms+ on budget devices.
 *
 * This is the PRIMARY signal for iOS/Safari where deviceMemory is hidden
 * and hardwareConcurrency can be spoofed.
 *
 * NOTE: This blocks the main thread for a few milliseconds. The hook
 * defers this call via requestIdleCallback so it never blocks first paint.
 */
function runMicroBenchmark(): number {
    const start = performance.now();
    let x = 0.1;
    for (let i = 0; i < 50_000; i++) {
        x += Math.sin(i) * Math.cos(i);
    }
    const elapsed = performance.now() - start;
    // Use x in a neutral condition to prevent JS engine from tree-shaking the loop
    return x !== 0.1 ? elapsed : elapsed;
}

// ── Main Detection Function ─────────────────────────────────────────────────

/**
 * Run all synchronous device capability checks and return a scored profile.
 * Called ONCE per session by the usePerformanceMode hook — results are cached
 * in sessionStorage to prevent re-execution on navigations/re-renders.
 */
export function detectDeviceSpecs(): DeviceSpecs {
    // SSR guard — return neutral "medium" tier on the server.
    // The hook's isLoading flag prevents Vanta from rendering until
    // client-side detection completes, avoiding hydration mismatch.
    if (typeof window === 'undefined') {
        return {
            os: 'Unknown', deviceType: 'Desktop', cpuCores: 4, ramGB: 4,
            gpu: 'Server', gpuVendor: 'Server', isLowSpec: false,
            performanceTier: 'medium', benchmarkTimeMs: 0,
            prefersReducedMotion: false, networkEffectiveType: '4g',
            saveData: false, screenPixelRatio: 1,
        };
    }

    const nav = navigator as ExtendedNavigator;

    // ── Collect signals ──────────────────────────────────────────────────────
    const os = detectOS(nav);
    const deviceType = detectDeviceType(nav);

    const cpuCores = nav.hardwareConcurrency || 4;
    // deviceMemory is Chromium-only. Safari/Firefox return undefined,
    // so we default to 4 (neutral) instead of 8 (optimistic) to avoid
    // falsely classifying an unknown 2 GB device as high-spec.
    const ramGB = nav.deviceMemory ?? 4;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const connection = nav.connection;
    const networkEffectiveType: string = connection?.effectiveType || '4g';
    const saveData: boolean = connection?.saveData || false;

    const screenPixelRatio = window.devicePixelRatio || 1;

    const { gpu, gpuVendor, isLowEndGPU } = detectGPU();
    const benchmarkTimeMs = runMicroBenchmark();

    // ── Weighted scoring system ──────────────────────────────────────────────
    //   Score 0–1  → high   (full Vanta 3D, all animations)
    //   Score 2–3  → medium (Vanta allowed but FPS-monitored by the hook)
    //   Score 4+   → low    (static gradient fallback, minimal animations)

    let score = 0;

    // Accessibility override — instant low tier (user explicitly asked for less motion)
    if (prefersReducedMotion) score += 10;

    // Benchmark speed (for 50k iterations: ~1-2ms on desktop, >2.5ms on budget mobile)
    if (benchmarkTimeMs > 2.5) score += 3;
    else if (benchmarkTimeMs > 1.5) score += 1;

    // RAM (only meaningful on Chromium; neutral 4 GB default on others)
    if (ramGB <= 2) score += 3;
    else if (ramGB <= 3) score += 2;
    else if (ramGB <= 4) score += 1;

    // CPU cores
    if (cpuCores <= 2) score += 3;
    else if (cpuCores <= 4) score += 1;

    // GPU
    if (isLowEndGPU) score += 3;

    // Network — loading Three.js (~500 KB) on slow network is bad UX
    if (saveData) score += 2;
    if (['slow-2g', '2g'].includes(networkEffectiveType)) score += 2;
    else if (networkEffectiveType === '3g') score += 1;

    // High DPI + weak hardware combo
    if (screenPixelRatio >= 2 && cpuCores <= 4 && ramGB <= 4) score += 1;

    // ── Tier classification ──────────────────────────────────────────────────
    let performanceTier: PerformanceTier;
    if (score >= 4) performanceTier = 'low';
    else if (score >= 2) performanceTier = 'medium';
    else performanceTier = 'high';

    return {
        os, deviceType, cpuCores, ramGB, gpu, gpuVendor,
        isLowSpec: performanceTier === 'low',
        performanceTier, benchmarkTimeMs,
        prefersReducedMotion, networkEffectiveType, saveData, screenPixelRatio,
    };
}
